import inventoryText from "../inventoryI18n.js";
import React, {
  useEffect,
  useMemo,
  useState,
} from "react";
import Modal from "react-bootstrap/Modal";
import axios from "axios";

import "./SupplierStatement.css";

const moneyFormatter = new Intl.NumberFormat(
  "en-US",
  {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }
);

const formatMoney = (value) => {
  const number = Number(value);

  return `$${moneyFormatter.format(
    Number.isFinite(number) ? number : 0
  )}`;
};

const formatDate = (value) => {
  if (!value) {
    return "—";
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return "—";
  }

  return date.toLocaleString();
};

const escapeHtml = (value) =>
  String(value === null || value === undefined
    ? ""
    : value
  ).replace(
    /[&<>"']/g,
    (character) => ({
      "&": "&amp;",
      "<": "&lt;",
      ">": "&gt;",
      '"': "&quot;",
      "'": "&#039;",
    })[character]
  );

function SupplierStatement({
  show,
  supplier,
  apiUrl,
  onClose,
}) {
  const [statement, setStatement] =
    useState(null);
  const [loading, setLoading] =
    useState(false);
  const [error, setError] = useState("");
  const [reloadKey, setReloadKey] =
    useState(0);

  useEffect(() => {
    let isMounted = true;

    if (
      !show ||
      !supplier ||
      !supplier.supplier_id
    ) {
      setStatement(null);
      setError("");
      setLoading(false);

      return () => {
        isMounted = false;
      };
    }

    const fetchStatement = async () => {
      setLoading(true);
      setError("");
      setStatement(null);

      try {
        const storedTokens = JSON.parse(
          localStorage.getItem("authTokens") ||
            "{}"
        );

        const response = await axios.get(
          `${apiUrl}/inventory/purchases/` +
            `suppliers/${supplier.supplier_id}/` +
            "statement/",
          {
            headers: {
              Authorization:
                `Bearer ${storedTokens.access}`,
            },
          }
        );

        if (isMounted) {
          setStatement(response.data);
        }
      } catch (requestError) {
        console.error(
          "Unable to load supplier statement:",
          requestError
        );

        const responseData =
          requestError.response &&
          requestError.response.data
            ? requestError.response.data
            : {};

        if (isMounted) {
          setError(
            responseData.error ||
              "Unable to load the supplier " +
                "statement. Please try again."
          );
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchStatement();

    return () => {
      isMounted = false;
    };
  }, [
    show,
    supplier,
    apiUrl,
    reloadKey,
  ]);

  const orders = useMemo(
    () =>
      statement &&
      Array.isArray(statement.orders)
        ? statement.orders
        : [],
    [statement]
  );

  const handlePrint = () => {
    if (!statement) {
      return;
    }

    const supplierData =
      statement.supplier || {};
    const supplierName =
      supplierData.company_name ||
      supplierData.full_name ||
      "Supplier";

    const orderRows = orders
      .map(
        (order) => `
          <tr>
            <td>${escapeHtml(
              formatDate(order.date)
            )}</td>
            <td>${escapeHtml(
              order.order_number
            )}</td>
            <td>${escapeHtml(
              order.request_number
            )}</td>
            <td>${escapeHtml(
              order.status_label
            )}</td>
            <td>${escapeHtml(
              order.description || "—"
            )}</td>
            <td class="money">${escapeHtml(
              formatMoney(
                order.purchases_debit_usd
              )
            )}</td>
            <td class="money">—</td>
            <td class="money">${escapeHtml(
              formatMoney(
                order.running_balance_usd
              )
            )}</td>
          </tr>
        `
      )
      .join("");

    const printableRows =
      orderRows ||
      `
        <tr>
          <td colspan="8" class="empty">
            No accepted purchase orders.
          </td>
        </tr>
      `;

    const printContents = `
      <!doctype html>
      <html>
        <head>
          <meta charset="utf-8" />
          <title>
            Supplier Statement - ${escapeHtml(
              supplierName
            )}
          </title>
          <style>
            @page {
              size: A4 landscape;
              margin: 12mm;
            }

            * {
              box-sizing: border-box;
            }

            body {
              margin: 0;
              color: #17212b;
              font-family: Arial, sans-serif;
              font-size: 12px;
            }

            .header {
              display: flex;
              justify-content: space-between;
              align-items: flex-start;
              padding-bottom: 16px;
              border-bottom: 3px solid #17384a;
            }

            .brand {
              color: #17384a;
              font-size: 24px;
              font-weight: 700;
              letter-spacing: 0.5px;
            }

            .generated {
              color: #5c6873;
              text-align: right;
              line-height: 1.5;
            }

            h1 {
              margin: 24px 0 16px;
              color: #17384a;
              text-align: center;
              font-size: 22px;
            }

            .supplier-info {
              display: grid;
              grid-template-columns: 1fr 1fr;
              gap: 8px 24px;
              margin-bottom: 18px;
              padding: 14px;
              border: 1px solid #d7dee4;
              background: #f6f8fa;
            }

            .supplier-info strong {
              color: #17384a;
            }

            .rule {
              grid-column: 1 / -1;
              color: #5c6873;
            }

            table {
              width: 100%;
              border-collapse: collapse;
            }

            thead {
              display: table-header-group;
            }

            tr {
              page-break-inside: avoid;
            }

            th {
              padding: 9px 7px;
              border: 1px solid #c9d1d8;
              background: #17384a;
              color: #ffffff;
              text-align: left;
              white-space: nowrap;
            }

            td {
              padding: 8px 7px;
              border: 1px solid #d7dee4;
              vertical-align: top;
            }

            tbody tr:nth-child(even) {
              background: #f7f9fa;
            }

            .opening td,
            .ending td {
              background: #e7edf1;
              font-weight: 700;
            }

            .money {
              text-align: right;
              white-space: nowrap;
            }

            .empty {
              padding: 24px;
              color: #66727c;
              text-align: center;
            }

            .footer {
              display: flex;
              justify-content: space-between;
              margin-top: 18px;
              padding-top: 12px;
              border-top: 1px solid #d7dee4;
              color: #66727c;
            }
          </style>
        </head>
        <body>
          <header class="header">
            <div class="brand">
              OMEGA SMART SYSTEMS
            </div>
            <div class="generated">
              Generated<br />
              ${escapeHtml(
                formatDate(
                  statement.generated_at
                )
              )}
            </div>
          </header>

          <h1>{inventoryText("inventory.SupplierAccountStatement")}</h1>

          <section class="supplier-info">
            <div>
              <strong>Supplier:</strong>
              ${escapeHtml(supplierName)}
            </div>
            <div>
              <strong>Currency:</strong>
              USD
            </div>
            <div>
              <strong>Contact:</strong>
              ${escapeHtml(
                supplierData.full_name || "—"
              )}
            </div>
            <div>
              <strong>Balance USD:</strong>
              ${escapeHtml(
                formatMoney(
                  statement.ending_balance_usd
                )
              )}
            </div>
          </section>

          <table>
            <thead>
              <tr>
                <th>{inventoryText("inventory.Date")}</th>
                <th>{inventoryText("inventory.Order")}</th>
                <th>{inventoryText("inventory.Request")}</th>
                <th>{inventoryText("inventory.Status")}</th>
                <th>{inventoryText("inventory.Description")}</th>
                <th>{inventoryText("inventory.PurchasesDebit")}</th>
                <th>{inventoryText("inventory.PaymentsCredit")}</th>
                <th>{inventoryText("inventory.BalanceUsd")}</th>
              </tr>
            </thead>
            <tbody>
              ${printableRows}
              <tr class="ending">
                <td colspan="7">{inventoryText("inventory.EndingBalanceUsd")}</td>
                <td class="money">
                  ${escapeHtml(
                    formatMoney(
                      statement
                        .ending_balance_usd
                    )
                  )}
                </td>
              </tr>
            </tbody>
          </table>

          <footer class="footer">
            <span>Omega Smart Systems</span>
            <span>
              ${orders.length}
              accepted purchase
              ${orders.length === 1
                ? "order"
                : "orders"}
            </span>
          </footer>
        </body>
      </html>
    `;

    const printFrame =
      document.createElement("iframe");

    printFrame.setAttribute(
      "title",
      "Supplier statement print"
    );
    printFrame.setAttribute(
      "aria-hidden",
      "true"
    );

    printFrame.style.position = "fixed";
    printFrame.style.right = "0";
    printFrame.style.bottom = "0";
    printFrame.style.width = "0";
    printFrame.style.height = "0";
    printFrame.style.border = "0";

    document.body.appendChild(printFrame);

    const printWindow =
      printFrame.contentWindow;

    if (!printWindow) {
      printFrame.remove();
      window.alert(
        "The supplier statement could not " +
          "be prepared for printing."
      );
      return;
    }

    const removePrintFrame = () => {
      if (printFrame.parentNode) {
        printFrame.remove();
      }
    };

    printWindow.addEventListener(
      "afterprint",
      removePrintFrame,
      { once: true }
    );

    printWindow.document.open();
    printWindow.document.write(printContents);
    printWindow.document.close();

    window.setTimeout(() => {
      printWindow.focus();
      printWindow.print();
    }, 250);
  };

  const titleSupplier =
    supplier &&
    (
      supplier.company_name ||
      supplier.fullname_en
    );

  return (
    <Modal
      show={show}
      onHide={onClose}
      className={
        "supplier-statement-modal " +
        "supplier-themed-modal"
      }
      centered
      size="xl"
    >
      <Modal.Header closeButton>
        <Modal.Title>
          {inventoryText("inventory.SupplierStatement")}
          {titleSupplier
            ? ` — ${titleSupplier}`
            : ""}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {loading ? (
          <div
            className="supplier-statement-state"
            role="status"
            aria-live="polite"
          >
            <span
              className={
                "supplier-statement-spinner"
              }
              aria-hidden="true"
            />
            <h3>Loading supplier statement</h3>
            <p>
              Retrieving accepted purchase orders
              and the current USD balance.
            </p>
          </div>
        ) : error ? (
          <div
            className={
              "supplier-statement-state " +
              "supplier-statement-error"
            }
            role="alert"
          >
            <h3>Statement could not be loaded</h3>
            <p>{error}</p>
            <button
              type="button"
              className={
                "supplier-modal-secondary"
              }
              onClick={() =>
                setReloadKey(
                  (current) => current + 1
                )
              }
            >{inventoryText("inventory.TryAgain")}</button>
          </div>
        ) : statement ? (
          <>
            <div
              className={
                "supplier-statement-summary"
              }
            >
              <article>
                <span>{inventoryText("inventory.AcceptedPurchases")}</span>
                <strong>
                  {formatMoney(
                    statement.purchases_total_usd
                  )}
                </strong>
              </article>
              <article>
                <span>{inventoryText("inventory.EndingBalanceUsd")}</span>
                <strong>
                  {formatMoney(
                    statement.ending_balance_usd
                  )}
                </strong>
              </article>
            </div>

            <div
              className={
                "supplier-statement-table-wrap"
              }
            >
              <table
                className={
                  "supplier-statement-table"
                }
              >
                <thead>
                  <tr>
                    <th>{inventoryText("inventory.Date")}</th>
                    <th>{inventoryText("inventory.Order")}</th>
                    <th>{inventoryText("inventory.Request")}</th>
                    <th>{inventoryText("inventory.Status")}</th>
                    <th>{inventoryText("inventory.Description")}</th>
                    <th>{inventoryText("inventory.PurchasesDebit")}</th>
                    <th>{inventoryText("inventory.PaymentsCredit")}</th>
                    <th>{inventoryText("inventory.BalanceUsd")}</th>
                  </tr>
                </thead>
                <tbody>
                  {orders.length === 0 ? (
                    <tr>
                      <td
                        colSpan="8"
                        className={
                          "supplier-statement-empty"
                        }
                      >
                        No accepted purchase orders.
                      </td>
                    </tr>
                  ) : (
                    orders.map((order) => (
                      <tr
                        key={
                          order.purchase_order_id
                        }
                      >
                        <td>
                          {formatDate(order.date)}
                        </td>
                        <td>
                          {order.order_number}
                        </td>
                        <td>
                          {order.request_number}
                        </td>
                        <td>
                          <span
                            className={
                              "supplier-statement-status " +
                              `supplier-statement-status-${order.status}`
                            }
                          >
                            {order.status_label}
                          </span>
                        </td>
                        <td>
                          {order.description || "—"}
                        </td>
                        <td
                          className={
                            "supplier-statement-money"
                          }
                        >
                          {formatMoney(
                            order
                              .purchases_debit_usd
                          )}
                        </td>
                        <td
                          className={
                            "supplier-statement-money"
                          }
                        >
                          —
                        </td>
                        <td
                          className={
                            "supplier-statement-money"
                          }
                        >
                          {formatMoney(
                            order
                              .running_balance_usd
                          )}
                        </td>
                      </tr>
                    ))
                  )}

                  <tr
                    className={
                      "supplier-statement-total-row"
                    }
                  >
                    <td colSpan="7">{inventoryText("inventory.EndingBalanceUsd")}</td>
                    <td>
                      {formatMoney(
                        statement.ending_balance_usd
                      )}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </>
        ) : null}
      </Modal.Body>

      <Modal.Footer>
        <button
          type="button"
          className="supplier-modal-secondary"
          onClick={onClose}
        >{inventoryText("inventory.Close")}</button>
        <button
          type="button"
          className={
            "supplier-statement-print-button"
          }
          disabled={!statement || loading}
          onClick={handlePrint}
        >{inventoryText("inventory.PrintStatement")}</button>
      </Modal.Footer>
    </Modal>
  );
}

export default SupplierStatement;
